import { z } from "zod";

const optionalTrimmedString = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    schema.optional()
  );

const updatePlatformPartSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_-]*$/);

const versionSchema = z.string().trim().min(1).max(80);
const optionalVersionSchema = optionalTrimmedString(versionSchema);

const desktopUpdateFileNameVersionPattern =
  /(?:^|[^0-9A-Za-z])v?(\d+\.\d+\.\d+(?:-[0-9A-Za-z][0-9A-Za-z.-]*)?(?:\+[0-9A-Za-z][0-9A-Za-z.-]*)?)(?=$|[^0-9A-Za-z])/;

const dateTimeStringSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => Number.isFinite(new Date(value).getTime()), {
    message: "日期时间不合法"
  });

const formBooleanSchema = z.preprocess((value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return value;
}, z.boolean());

export const adminDesktopUpdateReleaseSchema = z.object({
  version: versionSchema,
  target: updatePlatformPartSchema,
  arch: updatePlatformPartSchema,
  platformKey: optionalTrimmedString(z.string().trim().min(1).max(120)),
  channel: updatePlatformPartSchema.default("stable"),
  url: z.string().trim().url().max(2048),
  signature: z.string().trim().min(1).max(8192),
  notes: optionalTrimmedString(z.string().trim().max(16 * 1024)),
  pubDate: optionalTrimmedString(dateTimeStringSchema),
  minCurrentVersion: optionalTrimmedString(versionSchema),
  enabled: formBooleanSchema.default(true)
});

export const adminDesktopUpdateReleaseUploadSchema = z.object({
  version: optionalVersionSchema,
  target: updatePlatformPartSchema,
  arch: updatePlatformPartSchema,
  platformKey: optionalTrimmedString(z.string().trim().min(1).max(120)),
  channel: updatePlatformPartSchema.default("stable"),
  notes: optionalTrimmedString(z.string().trim().max(16 * 1024)),
  pubDate: optionalTrimmedString(dateTimeStringSchema),
  minCurrentVersion: optionalTrimmedString(versionSchema),
  enabled: formBooleanSchema.default(true)
});

export const adminDesktopUpdateReleaseUpdateSchema = adminDesktopUpdateReleaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "至少需要填写一个字段"
  });

export const adminDesktopUpdateReleaseParamsSchema = z.object({
  releaseId: z.string().trim().min(1)
});

export const adminDesktopUpdateReleaseListQuerySchema = z.object({
  target: updatePlatformPartSchema.optional(),
  arch: updatePlatformPartSchema.optional(),
  channel: updatePlatformPartSchema.optional(),
  includeDisabled: z
    .preprocess(
      (value) => {
        if (value === "true") return true;
        if (value === "false") return false;
        return value;
      },
      z.boolean()
    )
    .optional(),
  limit: z.coerce.number().int().min(1).max(200).optional()
});

export type AdminDesktopUpdateReleaseInput = z.infer<typeof adminDesktopUpdateReleaseSchema>;
export type AdminDesktopUpdateReleaseUploadInput = z.infer<
  typeof adminDesktopUpdateReleaseUploadSchema
>;
export type AdminDesktopUpdateReleaseUpdateInput = z.infer<
  typeof adminDesktopUpdateReleaseUpdateSchema
>;
export type AdminDesktopUpdateReleaseListQuery = z.infer<
  typeof adminDesktopUpdateReleaseListQuerySchema
>;

export interface DesktopUpdateManifest {
  version: string;
  notes?: string;
  pub_date?: string;
  platforms: Record<
    string,
    {
      url: string;
      signature: string;
    }
  >;
}

export interface AdminDesktopUpdateReleaseView {
  id: string;
  version: string;
  target: string;
  arch: string;
  platformKey: string;
  channel: string;
  url: string;
  signature: string;
  notes?: string;
  pubDate?: string;
  minCurrentVersion?: string;
  enabled: boolean;
  publishedByAdminId: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminDesktopUpdateReleaseListResponse {
  releases: AdminDesktopUpdateReleaseView[];
}

export function inferDesktopUpdateVersionFromFileName(fileName: string): string | null {
  const match = desktopUpdateFileNameVersionPattern.exec(fileName.trim());
  return match?.[1] ?? null;
}
